"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REVIEW_ANSWER_OPTIONS = void 0;
exports.answersFromJson = answersFromJson;
exports.answerFromFields = answerFromFields;
exports.reviewAnswers = reviewAnswers;
exports.answerRowFields = answerRowFields;
exports.answeredCount = answeredCount;
exports.priceBlockers = priceBlockers;
exports.retiredAnswers = retiredAnswers;
exports.questionFigure = questionFigure;
const LEGACY_SCHEMA_VERSION = 1;
/** hig_answer's options (global choice hig_reviewanswervalue), in schema order. */
exports.REVIEW_ANSWER_OPTIONS = ['Yes', 'No'];
const textOrNull = (value) => (typeof value === 'string' && value !== '' ? value : null);
/** The old JSON column's answers. Empty = none; any other version is refused rather than misread. */
function answersFromJson(text) {
    if (!text)
        return [];
    const parsed = JSON.parse(text);
    if (parsed.schemaVersion !== LEGACY_SCHEMA_VERSION) {
        throw new Error(`This version's review is in a format this app version can't read (schemaVersion ${String(parsed.schemaVersion)}).`);
    }
    return Object.entries(parsed.answers ?? {}).map(([key, entry]) => ({
        key,
        answer: entry.answer ?? null,
        comment: textOrNull(entry.comment),
        questionAsked: null,
    }));
}
/** A hig_reviewanswer row's fields as the device and the API's records carry them (the answer as its label). */
function answerFromFields(fields) {
    return {
        key: String(fields.hig_name ?? ''),
        answer: exports.REVIEW_ANSWER_OPTIONS.find((a) => a === fields.hig_answer) ?? null,
        comment: textOrNull(fields.hig_comment),
        questionAsked: textOrNull(fields.hig_questionasked),
        questionId: textOrNull(fields.hig_questionid),
    };
}
/**
 * Rows keyed by the question they look up, never by their name alone: a row
 * answers an asked question only when its question IS that question. A row
 * for a question no longer asked keeps its name, unless the name is an asked
 * question's key — then it answers nothing, so it can't stand in for it.
 */
function keyedByQuestion(rows, questions) {
    const keyById = new Map(questions.map((q) => [q.id.toLowerCase(), q.key]));
    const askedKeys = new Set(questions.map((q) => q.key));
    return rows.flatMap((row) => {
        const asked = row.questionId ? keyById.get(row.questionId.toLowerCase()) : undefined;
        if (asked)
            return [{ ...row, key: asked }];
        return askedKeys.has(row.key) ? [] : [row];
    });
}
/** A version's answers: its rows (by their question), and the old JSON's for any question with no row. */
function reviewAnswers(rows, legacy, questions) {
    const byKey = new Map(legacy.map((a) => [a.key, a]));
    for (const row of keyedByQuestion(rows, questions))
        byKey.set(row.key, row);
    return byKey;
}
/** The fields of a question's answer row: the wording saved is the one shown now. */
function answerRowFields(question, entry) {
    return {
        hig_name: question.key,
        hig_questionid: question.id,
        hig_answer: entry.answer,
        hig_comment: entry.comment,
        hig_questionasked: question.question,
    };
}
/** How many of the questions are answered (an answer to a question no longer asked doesn't count). */
function answeredCount(questions, answers) {
    return questions.filter((q) => answers.get(q.key)?.answer).length;
}
/** The questions still standing between the estimator and the sell price; empty = the price shows. */
function priceBlockers(questions, answers) {
    return questions.filter((q) => q.gatesPrice && answers.get(q.key)?.answer !== 'Yes');
}
/** Answers (or comments) to questions no longer asked, still part of this version's review. */
function retiredAnswers(questions, answers) {
    const asked = new Set(questions.map((q) => q.key));
    return [...answers.values()].filter((a) => !asked.has(a.key) && (a.answer || a.comment)).sort((a, b) => a.key.localeCompare(b.key));
}
const distinct = (values) => [...new Set(values)].sort((a, b) => a - b);
/** The figure a question shows (the template's REVIEW!I24, H42, I56, I62); null for one it doesn't know. */
function questionFigure(shows, sections, priced) {
    const surfaces = sections.flatMap((s) => s.surfaces);
    switch (shows) {
        case 'coats':
            return surfaces.length ? `${distinct(surfaces.map((s) => s.coats)).join(' / ')} coats` : null;
        case 'spreadRate':
            return surfaces.length ? `${distinct(surfaces.map((s) => s.spread)).join(' / ')} m²/L` : null;
        case 'hireTotal': {
            const hire = priced.breakdown.filter((b) => b.category === 'hire').reduce((sum, b) => sum + b.sell, 0);
            return `$${hire.toFixed(2)}`;
        }
        case 'overtimeHours':
            return `${priced.sections.reduce((sum, s) => sum + s.overtimeHours, 0).toFixed(2)} hrs`;
        default:
            return null;
    }
}
